import React, { useMemo, useState, useEffect } from "react";
import { cn } from "../../lib/cn";
import { Pagination } from "./Pagination";
import { Button } from "./Button";

/** Column filter dropdown API (antd-compatible subset) */
function FilterDropdownCtl({ col, columnKey, filterState, setFilterState }) {
  const cfg = filterState[columnKey];
  const selectedKeys = cfg?.selectedKeys ?? [];
  const visible = !!cfg?.open;

  function confirm() {
    setFilterState((s) => ({
      ...s,
      [columnKey]: { ...s[columnKey], open: false, appliedKeys: [...selectedKeys] },
    }));
  }

  function clearFilters() {
    setFilterState((s) => ({
      ...s,
      [columnKey]: { selectedKeys: [], appliedKeys: [], open: false },
    }));
  }

  if (!col.filterDropdown || !visible) return null;

  const node = col.filterDropdown({
    setSelectedKeys: (keys) =>
      setFilterState((prev) => ({
        ...prev,
        [columnKey]: { ...(prev[columnKey] || {}), selectedKeys: keys, open: true },
      })),
    selectedKeys,
    confirm,
    clearFilters,
  });

  return (
    <div
      className="absolute left-0 top-full z-40 mt-1 min-w-[200px] rounded-xl border border-slate-200 bg-white p-2 shadow-lg"
      onClick={(e) => e.stopPropagation()}
      role="presentation"
    >
      {node}
    </div>
  );
}

export function Table({
  columns = [],
  dataSource = [],
  loading,
  rowKey = "key",
  bordered,
  pagination: paginationProp,
  footer,
  locale,
  rowClassName,
  className,
  size,
  scroll,
}) {
  const emptyText = locale?.emptyText ?? "No data";

  /* filter state: columnKey -> { selectedKeys, appliedKeys?, open?: bool } */
  const [filterState, setFilterState] = useState({});
  const [sortState, setSortState] = useState({ columnKey: null, order: null });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(
    paginationProp?.pageSize ?? paginationProp?.defaultPageSize ?? 10
  );

  useEffect(() => {
    setPage(1);
  }, [dataSource]);

  useEffect(() => {
    setPageSize(
      paginationProp?.pageSize ?? paginationProp?.defaultPageSize ?? 10
    );
  }, [paginationProp?.pageSize, paginationProp?.defaultPageSize]);

  const filteredSorted = useMemo(() => {
    let rows = [...dataSource];
    columns.forEach((col, ci) => {
      const ck = String(col.key ?? col.dataIndex ?? ci);
      const applied =
        filterState[ck]?.appliedKeys ?? filterState[ck]?.selectedKeys;
      const hasApplied = applied && applied.length > 0;
      if (col.filters?.length && hasApplied && col.onFilter) {
        rows = rows.filter((r) =>
          applied.some((v) => col.onFilter(v, r))
        );
      } else if (col.filterDropdown && hasApplied && col.onFilter && applied[0] != null && applied[0] !== "") {
        rows = rows.filter((r) => col.onFilter(applied[0], r));
      }
    });

    const sc = columns.find((c, i) => String(c.key ?? c.dataIndex ?? i) === sortState.columnKey);
    if (sc?.sorter && sortState.order && sortState.columnKey != null) {
      const cmp = sc.sorter;
      rows.sort((a, b) =>
        sortState.order === "ascend" ? cmp(a, b) : cmp(b, a)
      );
    }
    return rows;
  }, [dataSource, columns, filterState, sortState]);

  const showPager = paginationProp && paginationProp !== false;
  const total = filteredSorted.length;

  let pageRows = filteredSorted;
  if (showPager) {
    const start = (page - 1) * pageSize;
    pageRows = filteredSorted.slice(start, start + pageSize);
  }

  const pagerNode =
    showPager &&
    paginationProp?.showTotal ? (
      <Pagination
        current={page}
        pageSize={pageSize}
        total={total}
        onChange={(p, sz) => {
          setPage(p);
          setPageSize(sz);
        }}
        pageSizeOptions={paginationProp.pageSizeOptions}
        showSizeChanger={paginationProp.showSizeChanger}
        showTotal={paginationProp.showTotal || (() => "")}
      />
    ) : showPager ? (
      <Pagination
        current={page}
        pageSize={pageSize}
        total={total}
        onChange={(p, sz) => {
          setPage(p);
          setPageSize(sz);
        }}
      />
    ) : null;

  function colKey(col, ci) {
    return String(col.key ?? col.dataIndex ?? ci);
  }

  function toggleFilterDropdown(ck, col) {
    if (!col.filterDropdown) return;
    setFilterState((s) => ({
      ...s,
      [ck]: {
        selectedKeys:
          s[ck]?.selectedKeys ??
          s[ck]?.appliedKeys ??
          [],
        appliedKeys: s[ck]?.appliedKeys ?? [],
        open: !s[ck]?.open,
      },
    }));
  }

  return (
    <div
      className={cn(
        "relative overflow-x-auto bg-transparent",
        scroll?.x ? "overflow-x-scroll" : null,
        bordered && "rounded-xl border border-slate-200",
        className
      )}
    >
      <table
        className={cn(
          "w-full border-collapse text-left text-sm text-slate-800",
          size === "small" ? "text-xs" : "",
          bordered && ""
        )}
        style={{
          minWidth: typeof scroll?.x === "number" ? scroll.x : undefined,
        }}
      >
        <thead>
          <tr
            className={cn(
              "border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wider text-slate-500",
              bordered ? "divide-x divide-slate-200 [&_th]:border-slate-200" : ""
            )}
          >
            {columns.map((col, ci) => {
              const ck = colKey(col, ci);
              const sorted =
                sortState.columnKey === ck ? sortState.order : null;

              function headerWrap(content) {
                return (
                  <div className="flex items-start justify-between gap-2 px-1 py-3">
                    <span className="flex-1 min-w-0">{content}</span>
                    <div className="relative flex shrink-0 items-center gap-1">
                      {col.sorter ? (
                        <button
                          type="button"
                          aria-label="Sort"
                          className="rounded p-1 text-slate-500 hover:bg-slate-100"
                          onClick={() => {
                            setSortState((prev) =>
                              prev.columnKey !== ck
                                ? { columnKey: ck, order: "ascend" }
                                : prev.order === "ascend"
                                  ? { columnKey: ck, order: "descend" }
                                  : { columnKey: null, order: null }
                            );
                          }}
                        >
                          {sorted === "ascend"
                            ? "▲"
                            : sorted === "descend"
                              ? "▼"
                              : "↕"}
                        </button>
                      ) : null}
                      {col.filterDropdown ? (
                        <button
                          type="button"
                          className="rounded p-1 text-slate-500 hover:bg-slate-100"
                          onClick={() => toggleFilterDropdown(ck, col)}
                        >
                          {col.filterIcon?.(!!filterState[ck]?.appliedKeys?.length) ?? "🔎"}
                        </button>
                      ) : null}
                      {col.filters?.length ? (
                        <FiltersMenu
                          col={col}
                          columnKey={ck}
                          filterState={filterState}
                          setFilterState={setFilterState}
                        />
                      ) : null}
                      {col.filterDropdown && filterState[ck]?.open ? (
                        <FilterDropdownCtl
                          col={col}
                          columnKey={ck}
                          filterState={filterState}
                          setFilterState={setFilterState}
                        />
                      ) : null}
                    </div>
                  </div>
                );
              }

              const titleInner =
                typeof col.title === "function" ? col.title() : col.title;

              return (
                <th
                  key={ck}
                  className={cn(
                    "px-4 text-left align-top",
                    bordered && "border-b border-slate-200"
                  )}
                  style={{ width: col.width }}
                >
                  {headerWrap(titleInner)}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody
          className={cn(
            bordered ? "divide-y divide-slate-200" : "divide-y divide-slate-100"
          )}
        >
          {loading ? (
            <tr>
              <td colSpan={columns.length} className="py-16 text-center text-slate-500">
                <span className="inline-flex items-center gap-2">
                  <span className="h-5 w-5 animate-spin rounded-full border-2 border-brand border-t-transparent" />
                  Loading…
                </span>
              </td>
            </tr>
          ) : pageRows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="py-12 text-center text-slate-500">
                {emptyText}
              </td>
            </tr>
          ) : (
            pageRows.map((record, rowIndex) => {
              const keyVal =
                typeof rowKey === "function"
                  ? rowKey(record)
                  : record[rowKey] ?? rowIndex;
              const rc =
                typeof rowClassName === "function"
                  ? rowClassName(record, rowIndex)
                  : rowClassName;
              return (
                <tr
                  key={keyVal}
                  className={cn(
                    "transition-colors hover:bg-slate-50/70",
                    rc
                  )}
                >
                  {columns.map((col, ci) => {
                    const val =
                      col.dataIndex != null
                        ? Array.isArray(col.dataIndex)
                          ? col.dataIndex.reduce((o, k) => (o == null ? o : o[k]), record)
                          : record[col.dataIndex]
                        : undefined;
                    const content = col.render
                      ? col.render(val, record, rowIndex)
                      : val;
                    return (
                      <td
                        key={colKey(col, ci)}
                        className={cn(
                          "px-4 py-3.5 align-middle text-slate-800",
                          bordered && "border-x border-slate-200 border-t border-slate-100"
                        )}
                      >
                        {content}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      {footer ? (
        <div className="border-t border-slate-100 px-4 py-3 text-sm text-slate-700">
          {footer()}
        </div>
      ) : null}
      {pagerNode ? (
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-100 px-4 py-3">
          {pagerNode}
        </div>
      ) : null}
    </div>
  );
}

function FiltersMenu({ col, columnKey, filterState, setFilterState }) {
  const [open, setOpen] = useState(false);

  function apply(value) {
    setFilterState((s) => ({
      ...s,
      [columnKey]: {
        selectedKeys: value === undefined ? [] : [value],
        appliedKeys: value === undefined ? [] : [value],
        open: false,
      },
    }));
    setOpen(false);
  }

  const active = !!filterState[columnKey]?.appliedKeys?.length;

  return (
    <div className="relative inline-block">
      <button
        type="button"
        className="rounded p-1 text-slate-500 hover:bg-slate-100"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((x) => !x);
        }}
      >
        {active ? "⧗" : "▾"}
      </button>
      {open ? (
        <div className="absolute right-0 z-40 mt-1 min-w-[160px] rounded-xl border border-slate-200 bg-white py-2 shadow-xl">
          {col.filters.map((f) => (
            <button
              key={String(f.value)}
              type="button"
              className="block w-full px-4 py-2 text-left text-sm hover:bg-brand-surface"
              onClick={() => apply(f.value)}
            >
              {f.text}
            </button>
          ))}
          <button
            type="button"
            className="mt-1 block w-full border-t px-4 py-2 text-left text-xs text-slate-500 hover:bg-slate-50"
            onClick={() => apply(undefined)}
          >
            Reset filter
          </button>
        </div>
      ) : null}
    </div>
  );
}
